import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useUnitAndLeaseInfo from '../../../../hooks/requests/lease/useUnitAndLeaseInfo';
import { DataUnitLeaseInfo } from '../../../../types/unit-rent-types';
import { useFinancialsStore } from '../../../../stores/FinancialIdentities/financialIdentities';
import useUserDetailsStore from '../../../../stores/UserDetails/userDetails';
import useFetchSignLease from '../../../../hooks/requests/lease/useFetchSignLease';
import moment from 'moment/moment';

export type SignLeaseType = {
  ticker: string | null;
  setTicker: (ticker: string | null) => void;
  leaseId: string | null;
  startDate: string | null;
  setStartDate: (date: string | null) => void;
  endDate: string | null;
  setEndDate: (date: string | null) => void;
  hasBankConnection: boolean;
  signButtonText: string;
  signButtonActive: boolean;
  onClickSign: () => void;
  loading: boolean;
  signing: boolean;
  data: DataUnitLeaseInfo | null;
  error: any;
  daysInFirstMonth: number;
  firstMonthRent: number;
};

const SignLeaseContext = createContext<SignLeaseType>({} as SignLeaseType);

export const SignLeaseProvider = ({ children }: { children: ReactNode }) => {
  const [ticker, setTicker] = useState<string | null>(null);
  const [leaseId, setLeaseId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [hasBankConnection, setHasBankConnection] = useState<boolean>(false);
  const [signButtonActive, setSignButtonActive] = useState<boolean>(false);
  const [signButtonText, setSignButtonText] = useState<string>('Sign lease');

  const { data, loading, error } = useUnitAndLeaseInfo(ticker || '');
  const { financialIdentities } = useFinancialsStore();
  const { userDetails } = useUserDetailsStore();
  const { request, signing, success } = useFetchSignLease();

  const onClickSign = async () => {
    if (!leaseId || !startDate || !endDate) return;
    setSignButtonActive(false);
    const body = {
      data: {
        attributes: {
          lease_id: leaseId,
          start_date: startDate,
          end_date: endDate,
          pay_first_payments: null,
        },
      },
    };
    try {
      const result = await request(body);
      if (result) {
        setSignButtonText('Lease signed successfully!');
      } else {
        setSignButtonText('Failed to sign lease.');
      }
    } catch (error) {
      setSignButtonActive(true);
    }
  };
  const daysInFirstMonth = useMemo(() => {
    const startDateMoment = moment(startDate, 'YYYY-MM-DD');
    const endDateMoment = moment(endDate, 'YYYY-MM-DD');
    const endOfMonth = moment(startDateMoment).endOf('month');

    const actualEnd = endDateMoment.isBefore(endOfMonth)
      ? endDateMoment
      : endOfMonth;

    return actualEnd.diff(startDateMoment, 'days') + 1;
  }, [startDate, endDate]);

  const firstMonthRent = useMemo(() => {
    const startDateMoment = moment(startDate, 'YYYY-MM-DD');
    const startOfMonth = moment(startDateMoment).startOf('month');
    const endOfMonth = moment(startDateMoment).endOf('month');

    const monthRent = data?.units[0]?.onMarketLease?.monthlyRentAmount;
    const daysInMonth = endOfMonth.diff(startOfMonth, 'days') + 1;
    const res = ((monthRent || 0) / daysInMonth) * daysInFirstMonth;

    return Math.round(res * 100) / 100;
  }, [startDate, daysInFirstMonth, data]);

  useEffect(() => {
    if (
      data &&
      data.units &&
      data.units.length > 0 &&
      data.units[0].onMarketLease !== null
    ) {
      const unitData = data.units[0];

      setLeaseId(unitData.onMarketLease.id || null);
      setStartDate(unitData.onMarketLease.startDate || null);
      setEndDate(unitData.onMarketLease.endDate || null);
    }
  }, [data]);

  useEffect(() => {
    if (
      financialIdentities?.relationships.default_external_financial_connection
        .data !== null
    ) {
      setHasBankConnection(true);
    }
    if (userDetails) {
      setSignButtonActive(true);
    }
  }, [userDetails, financialIdentities]);

  return (
    <SignLeaseContext.Provider
      value={{
        ticker,
        setTicker,
        leaseId,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        hasBankConnection,
        signButtonText,
        signButtonActive,
        onClickSign,
        loading,
        signing,
        data,
        error,
        daysInFirstMonth,
        firstMonthRent,
      }}
    >
      {children}
    </SignLeaseContext.Provider>
  );
};
const useSignLease = () => {
  return useContext(SignLeaseContext);
};
export default useSignLease;
